import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Gift, Copy, Check, UserPlus, Clock, Trophy, IndianRupee, Share2, Video, Wallet } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtDateTime } from "../../lib/api";
import { PageHeader, StatCard, LoadingRows, TableShell, TR, TD, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";

const CHIP = { pending: "bg-amber-500/10 text-amber-600", rewarded: "bg-emerald-500/10 text-emerald-600", flagged: "bg-red-500/10 text-red-500" };
const LABEL = { pending: "In Progress", rewarded: "Reward Earned", flagged: "Under Review" };

export default function CreatorReferrals() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/vid/creator/referrals").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!data) return <LoadingRows rows={4} />;
  const link = `${window.location.origin}${data.path}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); } catch { /* ignore */ }
    setCopied(true);
    toast.success("Referral link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-8" data-testid="creator-referrals-page">
      <PageHeader title="Refer & Earn" subtitle={`Invite creators — earn ₹${data.reward} for each friend who publishes ${data.required_videos} eligible videos`} testid="referrals-title" />

      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl p-6 text-white" style={{ background: "linear-gradient(135deg,#007A4D,#059669)" }} data-testid="referral-link-card">
        <p className="text-[11px] font-bold uppercase tracking-wider text-white/75">Your referral link</p>
        <div className="flex flex-col sm:flex-row gap-3 mt-3">
          <code data-testid="referral-link" className="flex-1 bg-black/25 rounded-xl px-4 py-3 text-sm font-mono truncate flex items-center">{link}</code>
          <Button onClick={copy} data-testid="referral-copy-btn" className="rounded-xl font-bold bg-white text-emerald-700 hover:bg-white/90 border-0 h-12">
            {copied ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}{copied ? "Copied!" : "Copy Link"}
          </Button>
        </div>
        <p className="text-xs text-white/70 mt-3">The reward is credited only after your friend publishes {data.required_videos} eligible videos — this keeps MPlay spam-free and fair.</p>
      </motion.div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={UserPlus} label="Creators Invited" value={data.stats.invited} testid="ref-stat-invited" tint="bg-violet-500/10 text-violet-500" />
        <StatCard icon={Clock} label="In Progress" value={data.stats.in_progress} testid="ref-stat-progress" delay={0.05} tint="bg-amber-500/10 text-amber-500" />
        <StatCard icon={Trophy} label="Qualified" value={data.stats.rewarded} testid="ref-stat-qualified" delay={0.1} tint="bg-emerald-500/10 text-emerald-500" />
        <StatCard icon={IndianRupee} label="Referral Earnings" value={`₹${data.stats.earned.toLocaleString("en-IN")}`} testid="ref-stat-earned" delay={0.15} tint="bg-blue-500/10 text-blue-500" />
      </div>

      <div className="grid sm:grid-cols-3 gap-4" data-testid="referral-steps">
        {[
          { icon: Share2, t: "1. Share your link", d: "Send your referral link to friends who create videos." },
          { icon: Video, t: `2. They publish ${data.required_videos} videos`, d: "Track each friend's live video progress below." },
          { icon: Wallet, t: `3. You earn ₹${data.reward}`, d: "Reward is auto-credited to your earnings, withdrawable with your balance." },
        ].map((s, i) => (
          <motion.div key={s.t} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            className="bg-card border border-border rounded-2xl p-5" data-testid={`ref-step-${i + 1}`}>
            <div className="h-10 w-10 rounded-xl flex items-center justify-center mb-3" style={{ background: "linear-gradient(135deg,#007A4D,#2563EB)" }}>
              <s.icon className="h-5 w-5 text-white" />
            </div>
            <p className="font-display font-bold text-sm">{s.t}</p>
            <p className="text-xs text-muted-foreground mt-1">{s.d}</p>
          </motion.div>
        ))}
      </div>

      <div>
        <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Gift className="h-5 w-5 text-emerald-500" /> Your Referrals</h2>
        {data.referrals.length === 0 ? (
          <EmptyState title="No referrals yet" body="Share your link — every qualified creator friend earns you a reward." />
        ) : (
          <TableShell head={["Creator", "Video Progress", "Reward", "Status", "Joined"]} testid="referrals-table">
            {data.referrals.map((r) => {
              const pct = Math.min(100, Math.round((r.videos_uploaded / (r.required_videos || 1)) * 100));
              return (
                <TR key={r.id} testid={`ref-row-${r.id}`}>
                  <TD><p className="font-semibold text-sm">{r.name}</p></TD>
                  <TD>
                    <div className="w-40 max-w-full" data-testid={`ref-progress-${r.id}`}>
                      <div className="flex justify-between text-[10px] font-bold mb-1">
                        <span>{r.videos_uploaded}/{r.required_videos} videos</span><span>{pct}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#007A4D,#22c55e)" }} />
                      </div>
                    </div>
                  </TD>
                  <TD><span className="font-bold">₹{r.reward}</span></TD>
                  <TD>
                    <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${CHIP[r.status] || CHIP.pending}`} data-testid={`ref-status-${r.id}`}>{LABEL[r.status] || r.status}</span>
                    {r.status === "flagged" && <p className="text-[10px] text-red-500 mt-1 max-w-[180px]">Flagged for fraud review — contact support if this is a mistake.</p>}
                  </TD>
                  <TD><span className="text-xs text-muted-foreground">{fmtDateTime(r.created_at)}</span></TD>
                </TR>
              );
            })}
          </TableShell>
        )}
      </div>
    </div>
  );
}
